import { ArrowDownToLine, Undo2 } from 'lucide-react';
import type { OpeningRow } from '@eombor/contracts';
import { Badge, Button, Card, EmptyState, Skeleton, Table, cn, type Column } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useCancelOpening, useOpenings } from '../model/opening.queries.js';

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

const dateText = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/**
 * KIRITILGAN ESKI QOLDIQ — hujjatlar ro'yxati.
 *
 * Ro'yxatning yagona vazifasi — BEKOR QILISH. Bir yuklashda ikki yuz
 * satr kiradi va ulardan birortasi xato bo'lsa, odam ikki yuztasini
 * bittalab qidirib yurmasligi kerak.
 *
 * Tugma `cancellable` ga qarab o'chadi va bu SERVERDAN keladi: ichidagi
 * biror narsa ishlatilgan, ko'chirilgan yoki hisobdan chiqarilgan
 * bo'lsa, qaytarish qoldiqni haqiqatdan ajratardi. Ekran buni taxmin
 * qilmaydi — aks holda odam tugmani bosib, uning ishlamasligini faqat
 * shundan keyin bilardi.
 */
export const OpeningsView = ({ onAdd }: { onAdd: () => void }) => {
  const list = useOpenings({ limit: 50 });
  const cancel = useCancelOpening();

  if (list.isPending) return <Skeleton className="h-64 w-full" />;
  if (list.isError) {
    return (
      <Card>
        <EmptyState title={t('opening.loadError')} description={list.error.message} />
      </Card>
    );
  }

  const rows = list.data?.items ?? [];
  if (rows.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<ArrowDownToLine size={20} aria-hidden />}
          title={t('opening.empty')}
          description={t('opening.emptyHint')}
          action={
            <Button variant="secondary" onClick={onAdd}>
              {t('opening.open')}
            </Button>
          }
        />
      </Card>
    );
  }

  const columns: Column<OpeningRow>[] = [
    { key: 'docNo', header: t('opening.colDocNo'), mono: true, width: '10rem' },
    {
      key: 'createdAt',
      header: t('opening.colCreatedAt'),
      mono: true,
      width: '8rem',
      render: (row) => dateText(row.createdAt),
    },
    {
      key: 'lineCount',
      header: t('opening.colLines'),
      align: 'right',
      mono: true,
      width: '6rem',
    },
    {
      key: 'totalQty',
      header: t('opening.colTotal'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (row) => qtyText(row.totalQty),
    },
    {
      key: 'createdByName',
      header: t('opening.colAuthor'),
      width: '13rem',
      render: (row) => <span className="block truncate">{row.createdByName ?? '—'}</span>,
    },
    {
      key: 'note',
      header: t('opening.colNote'),
      render: (row) => (
        <span className={cn('block truncate', row.note === null && 'text-text-faint')}>
          {row.note ?? '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('opening.colActions'),
      align: 'right',
      width: '12rem',
      sticky: 'right',
      render: (row) => {
        if (row.cancelledAt !== null) {
          return <Badge tone="neutral">{t('opening.cancelled')}</Badge>;
        }
        if (!row.cancellable) {
          /* Sabab AYTILADI: «nega o'chiq» degan savol aynan shu yerda
             tug'iladi va javobsiz qolgan o'chiq tugma xatodan farq
             qilmasdi. */
          return <span className="text-xs text-text-muted">{t('opening.locked')}</span>;
        }
        return (
          <Button
            variant="ghost"
            size="sm"
            loading={cancel.isPending && cancel.variables === row.id}
            onClick={() => cancel.mutate(row.id)}
          >
            <Undo2 size={14} aria-hidden />
            {t('opening.cancelDoc')}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" onClick={onAdd}>
          <ArrowDownToLine size={14} aria-hidden />
          {t('opening.open')}
        </Button>
      </div>
      <Table
        caption={t('opening.tab')}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        density="compact"
      />
    </div>
  );
};
